<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import TenantCard from "@/components/TenantCard.vue";
import { useUserStore } from "@/stores/userStore";
import { useToasterStore } from "@comp-lib/stores";
import { useModalStore } from "@comp-lib/stores/modalStore";
import { removeAccessToTenant } from "@/services/tenantManagementService";
import { storeToRefs } from "pinia";
import LoadingScreen from "@/components/LoadingScreen.vue";
import { HttpError } from "@/services/HttpError";
import type { MobiusTenant } from "@/types";

const router = useRouter();

const userStore = useUserStore();
const toasterStore = useToasterStore();
const modalStore = useModalStore();
const { hasAdminAccess, tenants } = storeToRefs(userStore);
const { logUserOut, setCurrentTenant, refreshToken } = userStore;
const { triggerNotif } = toasterStore;
const { openModal } = modalStore;

const isLoading = ref(false);

function handleLogout() {
  logUserOut();
}

function handleTenantSelect(tenant: MobiusTenant) {
  const redirected = setCurrentTenant(tenant, "/");
  if (!redirected) router.push("/");
}

function handleAdminConsoleSelect() {
  router.push("/admin");
}

function confirmUnlinkTenant(tenantId: number, tenantName: string) {
  openModal({
    title: "Unlink tenant",
    textContent: `Are you sure you want to unlink from "${tenantName}"? You will lose access to this tenant.`,
    confirmMethod: () => handleUnlinkTenant(tenantId, tenantName),
    confirmText: "Yes, unlink",
    destructive: true,
  });
}

async function handleUnlinkTenant(tenantId: number, tenantName: string) {
  isLoading.value = true;
  
  try {
    await removeAccessToTenant(tenantId);
    await refreshToken();
    
    triggerNotif({
      type: "success",
      title: "Tenant unlinked",
      bodyText: `Successfully unlinked from ${tenantName}.`
    });
  } catch (error) {
    console.error("Failed to unlink tenant:", error);
    triggerNotif({
      type: "error",
      title: "Failed to unlink tenant",
      bodyText: HttpError.getErrorMessage(error, "An unexpected error occurred.")
    });
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <LoadingScreen v-if="isLoading" />
  <div v-else class="login-body">
    <header>
      <img src="/src/assets/img/mobius.svg" alt="Gozynta Mobius logo" class="mobius-logo">
    </header>
    <div class="box">
      <div class="box-header">
        <h1 class="box-title">Which tenant?</h1>
        <p class="box-subtitle">Choose which of your tenants to log into</p>
      </div>
      <div class="box-content">
        <div class="tenant-list">
          <!-- Admin console option (only for admins) -->
          <TenantCard
            v-if="hasAdminAccess"
            admin
            @select="handleAdminConsoleSelect"
          />

          <!-- All available tenants -->
          <TenantCard
            v-for="tenant in tenants"
            :key="tenant.id"
            :tenant="tenant"
            :showUnlink="hasAdminAccess"
            @select="handleTenantSelect(tenant)"
            @unlink="confirmUnlinkTenant(tenant.id, tenant.name)"
          />
        </div>
      </div>
    </div>
    <p class="logout" @click="handleLogout">Log out</p>
  </div>
</template>

<style scoped lang="scss">
.login-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100vh;
  background-color: map.get(v.$gray, "100");
  header {
    padding: 40px 30px 50px;
    .mobius-logo {
      height: 40px;
    }
  }
  .box {
    background: white;
    border-radius: map.get(v.$border-radius, "md");
    padding: 30px;
    box-shadow: map.get(v.$shadow, "sm");
    width: 480px;
    max-width: 100%;
    margin: 0 auto;
    animation: fade-in .5s ease;
    .box-header {
      margin-bottom: 30px;
      text-align: center;
      .box-title {
        font-size: 1.75rem;
        line-height: 140%;
        font-weight: 700;
        margin-bottom: 10px;
      }
      .box-subtitle {
        color: map.get(v.$gray, "500");
        line-height: 1.5;
      }
    }
    .box-content {
      .tenant-list {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
    }
  }
  .logout {
    margin-top: 30px;
    font-size: 0.875rem;
    font-weight: 600;
    color: map.get(v.$primary, "600");
    text-decoration: underline;
    text-underline-offset: 5px;
    cursor: pointer;
    transition: color 0.2s;
    &:hover {
      color: map.get(v.$primary, "400");
    }
  }
}
@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
